import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeftIcon, DocumentTextIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { getSetList } from '../services/setlistApi';
import { getSongs, updateSong } from '../services/api';
import ChartViewModal from '../components/ChartViewModal';

const SetListViewPage = () => {
  const { id } = useParams();
  const [setlist, setSetlist] = useState(null);
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartModalSong, setChartModalSong] = useState(null);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [setlistData, songsData] = await Promise.all([
        getSetList(id),
        getSongs()
      ]);
      setSetlist(setlistData);
      setSongs(songsData);
    } catch (err) {
      console.error('Error loading setlist:', err);
      setError('Failed to load setlist');
    } finally {
      setLoading(false);
    }
  };

  const getSongById = (songId) => {
    return songs.find(song => song.id === songId);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleOpenChart = (song) => {
    setChartModalSong(song);
  };

  const handleCloseChart = () => {
    setChartModalSong(null);
  };

  const handleSaveChart = async (newChart) => {
    if (!chartModalSong) return;
    try {
      const updatedSong = await updateSong(chartModalSong.id, {
        ...chartModalSong,
        chart: newChart
      });
      setSongs(songs.map(s => s.id === updatedSong.id ? updatedSong : s));
      setChartModalSong(updatedSong);
    } catch (err) {
      console.error('Error saving chart:', err);
    }
  };

  const handleSaveLayout = async (newLayout) => {
    if (!chartModalSong) return;
    try {
      const updatedSong = await updateSong(chartModalSong.id, {
        ...chartModalSong,
        chart_layout: newLayout
      });
      setSongs(songs.map(s => s.id === updatedSong.id ? updatedSong : s));
      setChartModalSong(updatedSong);
    } catch (err) {
      console.error('Error saving layout:', err);
    }
  };

  const handleDeleteChart = async () => {
    if (!chartModalSong) return;
    try {
      const updatedSong = await updateSong(chartModalSong.id, {
        ...chartModalSong,
        chart: null,
        chart_layout: null
      });
      setSongs(songs.map(s => s.id === updatedSong.id ? updatedSong : s));
      setChartModalSong(null);
    } catch (err) {
      console.error('Error deleting chart:', err);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-8">Loading setlist...</div>
      </div>
    );
  }

  if (error || !setlist) {
    return (
      <div className="p-6">
        <div className="text-center py-8 text-red-600">{error || 'Setlist not found'}</div>
        <div className="text-center">
          <Link to="/setlists" className="text-blue-600 hover:text-blue-800">
            Back to Set Lists
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/setlists"
          className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-4"
        >
          <ArrowLeftIcon className="w-4 h-4 mr-2" />
          Back to Set Lists
        </Link>

        <div className="flex items-center space-x-4">
          <h1 className="text-2xl font-bold text-gray-900">{setlist.name}</h1>
          <div className="flex items-center text-gray-500">
            <CalendarIcon className="w-5 h-5 mr-2" />
            {formatDate(setlist.date)}
          </div>
        </div>
      </div>

      {/* Sets */}
      <div className="space-y-6">
        {setlist.sets?.map((set, setIndex) => (
          <div key={setIndex} className="bg-white rounded-lg shadow overflow-hidden">
            <div className="bg-blue-50 px-6 py-3 border-b">
              <h2 className="text-lg font-semibold text-gray-900">
                {set.name || `Set ${setIndex + 1}`}
              </h2>
            </div>

            <div className="divide-y divide-gray-200">
              {set.items?.map((item, itemIndex) => {
                if (item.type === 'banter') {
                  return (
                    <div key={itemIndex} className="px-6 py-3 bg-gray-50">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500 italic">
                          {item.banter?.label || 'Banter'}
                        </span>
                        {item.banter?.duration && (
                          <span className="text-sm text-gray-400">
                            {formatDuration(item.banter.duration)}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                }

                if (item.type === 'song' && item.song_id) {
                  const song = getSongById(item.song_id);
                  if (!song) {
                    return (
                      <div key={itemIndex} className="px-6 py-4 text-gray-400 italic">
                        Unknown song
                      </div>
                    );
                  }

                  return (
                    <div key={itemIndex} className="px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{song.title}</div>
                          {song.artist && (
                            <div className="text-sm text-gray-500">{song.artist}</div>
                          )}
                        </div>

                        <div className="flex items-center space-x-4">
                          {song.duration && (
                            <span className="text-sm text-gray-400">
                              {formatDuration(song.duration)}
                            </span>
                          )}

                          {song.chart && (
                            <button
                              onClick={() => handleOpenChart(song)}
                              className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 text-sm"
                            >
                              <DocumentTextIcon className="w-4 h-4 mr-1" />
                              Chart
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }

                return null;
              })}

              {(!set.items || set.items.length === 0) && (
                <div className="px-6 py-4 text-gray-400 italic">
                  No songs in this set
                </div>
              )}
            </div>
          </div>
        ))}

        {(!setlist.sets || setlist.sets.length === 0) && (
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
            This setlist has no sets yet
          </div>
        )}
      </div>

      {/* Chart View Modal */}
      {chartModalSong && (
        <ChartViewModal
          isOpen={!!chartModalSong}
          onClose={handleCloseChart}
          chart={chartModalSong.chart}
          chartLayout={chartModalSong.chart_layout}
          songTitle={chartModalSong.title}
          onSave={handleSaveChart}
          onDelete={handleDeleteChart}
          onSaveLayout={handleSaveLayout}
        />
      )}
    </div>
  );
};

export default SetListViewPage;
